import { ReactNode } from 'react';

import PhotoAlbumIcon from '@mui/icons-material/PhotoAlbum';

import isEmpty from 'lodash/isEmpty';

import { LocationResponse } from '@/api/data-contracts';
import { ReactComponent as CalendarIcon } from '@/assets/icons/calendar-icon.svg';
import { ReactComponent as CompaniesIcon } from '@/assets/icons/companies-icon.svg';
import { ReactComponent as DashboardIcon } from '@/assets/icons/dashboard-icon.svg';
import { ReactComponent as FinancialIcon } from '@/assets/icons/financial-icon.svg';
import { ReactComponent as LocationIcon } from '@/assets/icons/locations-icon.svg';
import { ReactComponent as MediaPlansIcon } from '@/assets/icons/media-plans-icon.svg';
import { ReactComponent as AdvertisingPackagesIcon } from '@/assets/icons/menu-adv-packages.svg';
import { ReactComponent as AdvertisingSpacesIcon } from '@/assets/icons/menu-adv-spaces.svg';
import { ReactComponent as MenuClientsIcon } from '@/assets/icons/menu-clients-icon.svg';
import { ReactComponent as MenuCommercialConditionsIcon } from '@/assets/icons/menu-commercial-conditions-icon.svg';
import { ReactComponent as MenuCompaniesIcon } from '@/assets/icons/menu-companies-icon.svg';
import { ReactComponent as LocationsIcon } from '@/assets/icons/menu-locations.svg';
import { ReactComponent as MenuPortfolioIcon } from '@/assets/icons/menu-portfolio-icon.svg';
import { ReactComponent as MenuRebatePoliciesIcon } from '@/assets/icons/menu-rebate-policies-icon.svg';
import { ReactComponent as MenuServiceSuppliersIcon } from '@/assets/icons/menu-service-suppliers-icon.svg';
import { ReactComponent as MenuServiceTasksIcon } from '@/assets/icons/menu-service-tasks-icon.svg';
import { ReactComponent as MenuTeamIcon } from '@/assets/icons/menu-team-icon.svg';
import { ReactComponent as MenuTenantsIcon } from '@/assets/icons/menu-tenants-icon.svg';
import { ReactComponent as ReservationsIcon } from '@/assets/icons/reservations-icon.svg';
import { ReactComponent as ServicesIcon } from '@/assets/icons/services-icon.svg';
import { ReactComponent as SettingsIcon } from '@/assets/icons/settings-icon.svg';
import { ReactComponent as StatisticsIcon } from '@/assets/icons/statistics-icon.svg';
import { ReactComponent as UsersIcon } from '@/assets/icons/users-icon.svg';
import { ROUTES } from '@/config/routes';

export interface SideMenuItem {
  label: string;
  tooltip?: string;
  path?: string;
  otherPath?: string;
  icon?: ReactNode;
  relatedRoutes?: string[];
  submenus?: SideMenuItem[];
  hidden?: boolean;
  disabled?: boolean;
  isStatisticsMenu?: boolean;
}

export interface SideMenuSection {
  menuItems: SideMenuItem[];
  flex?: number;
}

export interface SideMenuPermissions {
  canCreatePastBookings?: boolean;
}
const getAdvertisersMenuSections = (t: any): SideMenuSection[] => [
  {
    menuItems: [
      {
        label: t('dashboard'),
        path: ROUTES.AUTHORIZED.LANDING,
        icon: <DashboardIcon />,
      },
      {
        label: t('locations'),
        icon: <LocationIcon />,
        path: ROUTES.AUTHORIZED.ADVERTISING_SPACES,
        submenus: [
          {
            label: t('Advertising Spaces'),
            path: ROUTES.AUTHORIZED.ADVERTISING_SPACES,
            icon: <AdvertisingSpacesIcon />,
          },
          {
            label: t('Advertising Packages'),
            path: ROUTES.AUTHORIZED.ADVERTISING_PACKAGES,
            icon: <AdvertisingPackagesIcon />,
          },
        ],
      },
    ],
  },
  {
    menuItems: [
      {
        label: t('media_plans'),
        path: ROUTES.AUTHORIZED.MEDIA_PLANS,
        icon: <MediaPlansIcon />,
      },
      {
        label: t('orders_page'),
        path: ROUTES.AUTHORIZED.ORDERS,
        icon: <ReservationsIcon />,
      },
    ],
  },
  {
    flex: 1,
    menuItems: [
      {
        label: t('Settings'),
        path: ROUTES.AUTHORIZED.COMPANY_PROFILE,
        icon: <SettingsIcon />,
        submenus: [
          {
            label: t('clients'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/clients`,
            icon: <MenuClientsIcon />,
            submenus: [
              {
                label: t('company_client_form'),
                path: ROUTES.AUTHORIZED.COMPANY_CLIENT_FORM,
                hidden: true,
              },
              {
                label: t('remove_company_client'),
                path: ROUTES.AUTHORIZED.DELETE_COMPANY_CLIENT,
                hidden: true,
              },
            ],
          },
          {
            label: t('Companies'),
            path: ROUTES.AUTHORIZED.COMPANY_PROFILE,
            otherPath: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/terms`,
            icon: <MenuCompaniesIcon />,
          },
          {
            label: t('team'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/team`,
            icon: <MenuTeamIcon />,
          },
          {
            label: t('tenants'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/tenants`,
            icon: <MenuTenantsIcon />,
          },
          {
            label: t('commercial_conditions'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/settings`,
            icon: <MenuCommercialConditionsIcon />,
          },
          {
            label: t('company_form'),
            path: ROUTES.AUTHORIZED.COMPANY_FORM,
            hidden: true,
          },
          {
            label: t('company_terms'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/terms`,
            hidden: true,
          },
        ],
      },
      {
        label: t('Calendar'),
        path: ROUTES.AUTHORIZED.CALENDAR,
        icon: <CalendarIcon style={{ width: '1.25rem' }} />,
      },
    ],
  },
];

const getServicesMenuSections = (t: any, canViewServiceSuppliers: boolean): SideMenuSection[] => [
  {
    menuItems: [
      {
        label: t('dashboard'),
        path: ROUTES.AUTHORIZED.LANDING,
        icon: <DashboardIcon />,
      },
      {
        label: t('services'),
        path: ROUTES.AUTHORIZED.SERVICES,
        icon: <ServicesIcon />,
      },
      {
        label: t('Service Tasks'),
        path: ROUTES.AUTHORIZED.SERVICE_ORDERS,
        icon: <MenuServiceTasksIcon />,
      },
      ...(canViewServiceSuppliers
        ? [
            {
              label: t('Service Suppliers'),
              path: ROUTES.AUTHORIZED.SERVICE_SUPPLIERS,
              icon: <MenuServiceSuppliersIcon />,
            },
          ]
        : []),
    ],
  },
  {
    flex: 1,
    menuItems: [
      {
        label: t('Settings'),
        path: ROUTES.AUTHORIZED.COMPANY_PROFILE,
        icon: <SettingsIcon />,
        submenus: [
          {
            label: t('Companies'),
            path: ROUTES.AUTHORIZED.COMPANY_PROFILE,
            otherPath: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/terms`,
            icon: <MenuCompaniesIcon />,
          },
          {
            label: t('team'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/team`,
            icon: <MenuTeamIcon />,
          },
          {
            label: t('company_form'),
            path: ROUTES.AUTHORIZED.COMPANY_FORM,
            hidden: true,
          },
          {
            label: t('company_terms'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/terms`,
            hidden: true,
          },
        ],
      },
      {
        label: t('Calendar'),
        path: ROUTES.AUTHORIZED.CALENDAR,
        icon: <CalendarIcon style={{ width: '1.25rem' }} />,
      },
    ],
  },
];

const getAdminSideMenuSections = (t: any): SideMenuSection[] => [
  {
    menuItems: [
      {
        label: t('dashboard'),
        path: ROUTES.AUTHORIZED.LANDING,
        icon: <DashboardIcon />,
      },
      {
        label: t('users'),
        path: `${ROUTES.AUTHORIZED.ADMIN_USERS}/team`,
        icon: <UsersIcon />,
        submenus: [
          {
            label: t('clients'),
            path: `${ROUTES.AUTHORIZED.ADMIN_USERS}/clients`,
            icon: <MenuClientsIcon />,
          },
          {
            label: t('team'),
            path: `${ROUTES.AUTHORIZED.ADMIN_USERS}/team`,
            icon: <MenuTeamIcon />,
          },
        ],
      },
      {
        label: t('Settings'),
        path: ROUTES.AUTHORIZED.ADMIN_SIGNUP_REQUESTS,
        icon: <CompaniesIcon />,
      },
    ],
  },
  {
    menuItems: [
      {
        label: t('financial'),
        path: ROUTES.AUTHORIZED.FINANCIAL,
        icon: <FinancialIcon />,
        disabled: true,
      },
    ],
  },
  {
    menuItems: [
      {
        label: t('locations'),
        path: ROUTES.AUTHORIZED.COMPANY_LOCATIONS,
        icon: <LocationIcon />,
      },
      {
        label: t('ad_spaces'),
        path: ROUTES.AUTHORIZED.ADVERTISING_SPACES,
        icon: <PhotoAlbumIcon style={{ width: '1.25rem', height: '1.25rem' }} />,
      },
      {
        label: t('Advertising Packages'),
        path: ROUTES.AUTHORIZED.ADVERTISING_PACKAGES,
        icon: <PhotoAlbumIcon style={{ width: '1.25rem', height: '1.25rem' }} />,
      },
      {
        label: t('services'),
        path: ROUTES.AUTHORIZED.ADMIN_SERVICES,
        icon: <ServicesIcon />,
      },
      {
        label: t('orders_page'),
        path: ROUTES.AUTHORIZED.ORDERS,
        icon: <ReservationsIcon />,
      },
      {
        label: t('media_plans'),
        path: ROUTES.AUTHORIZED.MEDIA_PLANS,
        icon: <MediaPlansIcon />,
      },
      {
        label: t('Service Tasks'),
        path: ROUTES.AUTHORIZED.SERVICE_ORDERS,
        icon: <MenuServiceTasksIcon />,
      },
      {
        label: t('Service Suppliers'),
        path: ROUTES.AUTHORIZED.SERVICE_SUPPLIERS,
        icon: <MenuServiceSuppliersIcon />,
      },
    ],
  },
  {
    flex: 1,
    menuItems: [
      {
        label: t('settings'),
        path: ROUTES.AUTHORIZED.ADMIN_SETTINGS,
        icon: <SettingsIcon />,
      },
      {
        label: t('Calendar'),
        path: ROUTES.AUTHORIZED.CALENDAR,
        icon: <CalendarIcon style={{ width: '1.25rem' }} />,
      },
    ],
  },
];

const getLocationSideMenuSections = (
  t: any,
  organizationName: string,
  malls: Record<string, LocationResponse[]>,
): SideMenuSection[] => {
  const locationMenuItems: SideMenuSection[] = [
    {
      menuItems: [
        {
          label: t('dashboard'),
          path: ROUTES.AUTHORIZED.LANDING,
          icon: <DashboardIcon />,
        },
        {
          label: t('portofolio'),
          icon: <MenuPortfolioIcon />,
          path: ROUTES.AUTHORIZED.COMPANY_LOCATIONS,
          submenus: [
            {
              label: t('Locations'),
              path: ROUTES.AUTHORIZED.COMPANY_LOCATIONS,
              icon: <LocationsIcon />,
            },
            {
              label: t('Advertising Spaces'),
              path: ROUTES.AUTHORIZED.ADVERTISING_SPACES,
              icon: <AdvertisingSpacesIcon />,
            },
            {
              label: t('Advertising Packages'),
              path: ROUTES.AUTHORIZED.ADVERTISING_PACKAGES,
              icon: <AdvertisingPackagesIcon />,
            },
          ],
        },
        {
          label: t('media_plans'),
          path: ROUTES.AUTHORIZED.MEDIA_PLANS,
          icon: <MediaPlansIcon />,
        },
        {
          label: t('orders_page'),
          path: ROUTES.AUTHORIZED.ORDERS,
          icon: <ReservationsIcon />,
        },
        ...(organizationName && !isEmpty(malls) && Object.values(malls).flat().length > 0
          ? [
              {
                label: t('statistics'),
                path: ROUTES.AUTHORIZED.STATISTICS,
                icon: <StatisticsIcon />,
                isStatisticsMenu: true,
                submenus: [
                  {
                    label: organizationName,
                    submenus: Object.keys(malls).map((group) => ({
                      label: group,
                      submenus: malls[group].map((location) => ({
                        label: location.locationName!,
                        path: `${ROUTES.AUTHORIZED.STATISTICS}/${location.id}`,
                      })),
                    })),
                  },
                ],
              },
            ]
          : []),
      ],
    },
  ];

  locationMenuItems.push({
    flex: 1,
    menuItems: [
      {
        label: t('Settings'),
        path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/locations`,
        icon: <SettingsIcon />,
        submenus: [
          {
            label: t('locations'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/locations`,
            icon: <LocationsIcon />,
          },
          {
            label: t('clients'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/clients`,
            icon: <MenuClientsIcon />,
            submenus: [
              {
                label: t('company_client_form'),
                path: ROUTES.AUTHORIZED.COMPANY_CLIENT_FORM,
                hidden: true,
              },
              {
                label: t('remove_company_client'),
                path: ROUTES.AUTHORIZED.DELETE_COMPANY_CLIENT,
                hidden: true,
              },
            ],
          },
          {
            label: t('Companies'),
            path: ROUTES.AUTHORIZED.COMPANY_PROFILE,
            otherPath: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/terms`,
            icon: <MenuCompaniesIcon />,
          },
          {
            label: t('team'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/team`,
            icon: <MenuTeamIcon />,
          },
          {
            label: t('commercial_conditions'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/discounts`,
            icon: <MenuCommercialConditionsIcon />,
          },
          {
            label: t('rebate_policies'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/rebate-policies`,
            icon: <MenuRebatePoliciesIcon />,
          },
          {
            label: t('companys_form'),
            path: ROUTES.AUTHORIZED.COMPANY_FORM,
            hidden: true,
          },
          {
            label: t('company_terms'),
            path: `${ROUTES.AUTHORIZED.COMPANY_PROFILE}/terms`,
            hidden: true,
          },
        ],
      },
      {
        label: t('Calendar'),
        path: ROUTES.AUTHORIZED.CALENDAR,
        icon: <CalendarIcon style={{ width: '1.25rem' }} />,
      },
    ],
  });

  return locationMenuItems;
};

export {
  getLocationSideMenuSections,
  getAdminSideMenuSections,
  getServicesMenuSections,
  getAdvertisersMenuSections,
};
